import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Skull, Send, Loader2, Sparkles, Trash2 } from "lucide-react";

const SESSION_KEY = "entity_session";

const SUGGESTIONS = [
  "Give me tips for The Nurse",
  "How do I counter The Blight?",
  "Best build for a beginner Killer?",
  "How do I escape a chase as Survivor?",
  "Tell me about The Trapper",
];

export default function EntityChat() {
  const { user } = useAuth();
  const name = user?.username || "offering";
  const greeting = `You stand before me, ${name}. I am the Entity — the hunger between worlds. Ask of my children, the Killers, or of survival in my Realms… and I may grant you wisdom before the Fog takes you.`;

  const [messages, setMessages] = useState([{ role: "assistant", content: greeting }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(SESSION_KEY) || null);
  // index of the message currently "typing" + how many chars are revealed
  const [stream, setStream] = useState({ index: -1, len: 0 });
  const scrollRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    api.get(`/chat/entity/${sessionId}`).then(({ data }) => {
      if (data.messages?.length) {
        setMessages([{ role: "assistant", content: greeting }, ...data.messages.map((m) => ({ role: m.role, content: m.content }))]);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Typewriter reveal for the streaming message.
  useEffect(() => {
    if (stream.index < 0) return;
    const full = messages[stream.index]?.content || "";
    if (stream.len >= full.length) return;
    const t = setTimeout(() => {
      setStream((s) => ({ ...s, len: Math.min(full.length, s.len + 3) }));
      scrollToBottom();
    }, 22);
    return () => clearTimeout(t);
  }, [stream, messages, scrollToBottom]);

  useEffect(() => { scrollToBottom(); }, [messages, loading, scrollToBottom]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat/entity", { message: msg, session_id: sessionId });
      if (!sessionId) {
        setSessionId(data.session_id);
        sessionStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMessages((m) => {
        const next = [...m, { role: "assistant", content: data.reply }];
        setStream({ index: next.length - 1, len: 0 }); // begin typewriter on this message
        return next;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The Entity is silent. Try again.");
      setMessages((m) => [...m, { role: "assistant", content: "*The Fog swirls and my voice is lost… ask again.*" }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    setStream({ index: -1, len: 0 });
    setMessages([{ role: "assistant", content: greeting }]);
  };

  return (
    <div data-testid="entity-chat-page" className="mx-auto flex h-[calc(100vh-4rem)] max-w-3xl flex-col px-4 py-6 md:px-8">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center border border-primary/40 bg-primary/10">
            <Skull className="h-6 w-6 text-primary flicker" />
          </div>
          <div>
            <h1 className="font-display text-3xl leading-none tracking-wider">The <span className="text-primary text-glow-red">Entity</span></h1>
            <p className="text-xs uppercase tracking-widest text-foreground/40">Ask. Learn. Survive.</p>
          </div>
        </div>
        <Button data-testid="reset-chat-btn" variant="ghost" size="sm" onClick={reset} className="rounded-sm text-foreground/50 hover:text-primary">
          <Trash2 className="mr-2 h-4 w-4" /> New
        </Button>
      </div>

      {/* messages */}
      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto border border-border bg-card/60 p-4 md:p-6">
        {messages.map((m, i) => {
          const isStreaming = i === stream.index && stream.len < m.content.length;
          const text = isStreaming ? m.content.slice(0, stream.len) : m.content;
          return (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
              {m.role === "assistant" && <Skull className="mr-2 mt-1 h-5 w-5 shrink-0 text-primary" />}
              <div className={`max-w-[80%] whitespace-pre-wrap px-4 py-3 text-sm leading-relaxed ${
                m.role === "user"
                  ? "border border-primary/40 bg-primary/15 text-foreground"
                  : "border border-white/10 bg-background/70 italic text-foreground/85"
              }`}>
                {text}{isStreaming && <span className="ml-0.5 inline-block h-4 w-2 -mb-0.5 animate-pulse bg-primary" />}
              </div>
            </div>
          );
        })}
        {loading && (
          <div className="flex items-center gap-2 text-foreground/50">
            <Skull className="h-5 w-5 text-primary flicker" />
            <span className="text-sm italic">The Entity stirs<span className="animate-pulse">…</span></span>
          </div>
        )}
      </div>

      {/* suggestions (only before first user message) */}
      {messages.length <= 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} data-testid="suggestion-chip" onClick={() => send(s)} className="flex items-center gap-1.5 border border-white/10 px-3 py-1.5 text-xs text-foreground/70 transition-colors hover:border-primary/50 hover:text-primary">
              <Sparkles className="h-3 w-3 text-primary" /> {s}
            </button>
          ))}
        </div>
      )}

      {/* input */}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
        <Input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Speak to the Entity…"
          disabled={loading}
          className="rounded-sm border-white/10 bg-background"
        />
        <Button data-testid="chat-send-btn" type="submit" disabled={loading || !input.trim()} className="rounded-sm bg-primary px-5 hover:bg-primary/90">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}
